import {browserNotificationPermissionSignal} from '../signals/Notification.signals';
import {logger} from '../logger';

const toNotificationPermission = (
    state: PermissionState,
): NotificationPermission => (state === 'prompt' ? 'default' : state);

export type BrowserNotificationPermission =
    NotificationPermission | 'unsupported';

const initPermission = (): BrowserNotificationPermission => {
    if (typeof Notification === 'undefined') {
        return 'unsupported';
    }
    return Notification.permission;
};

export const createBrowserNotificationService = () => {
    let permission: BrowserNotificationPermission = initPermission();

    const setPermission = (value: BrowserNotificationPermission) => {
        if (permission === value) {
            return;
        }
        permission = value;
        browserNotificationPermissionSignal.emit();
    };

    const getPermissionState = () => permission;

    const requestPermission = async () => {
        if (permission !== 'default') {
            return;
        }
        const result = await Notification.requestPermission();
        setPermission(result);
    };

    const sendNotification = (
        title: string,
        options?: NotificationOptions,
    ): Notification | undefined => {
        if (permission !== 'granted') {
            return undefined;
        }
        return new Notification(title, options);
    };

    if (
        typeof navigator !== 'undefined' &&
        typeof navigator.permissions?.query === 'function'
    ) {
        navigator.permissions
            .query({name: 'notifications'})
            .then(result => {
                setPermission(toNotificationPermission(result.state));
                result.onchange = () => {
                    setPermission(toNotificationPermission(result.state));
                };
            })
            .catch(e => {
                logger.info(
                    e,
                    'Browser does not support permissions API for notifications',
                );
            });
    }

    return {
        getPermissionState,
        requestPermission,
        sendNotification,
    };
};

export const browserNotificationService = createBrowserNotificationService();

export type BrowserNotificationService = ReturnType<
    typeof createBrowserNotificationService
>;
